/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Helper function to wait for an element to be available
function waitForElement(selector, timeout = 10000) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const element = selector();

      if (element) {
        clearInterval(interval);
        resolve(element);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        reject(new Error("Timed out waiting for element"));
      }
    }, 100);
  });
}

// Update Gorgias styles
var HIDE_POWERED_BY_GORGIAS = true;
async function updateGorigasStyles() {
  try {
    // Wait for Gorgias chat to initialize
    await (window.GorgiasChat
      ? window.GorgiasChat.init()
      : new Promise((resolve) => {
          window.addEventListener("gorgias-widget-loaded", resolve);
        }));
    // Wait for the chat button head element to be available and have emotion attributes
    const chatButtonHead = await waitForElement(() => {
      const head = document
        .querySelector("#gorgias-chat-container")
        ?.querySelector("#chat-button")
        ?.contentWindow.document.querySelector("head");
      return head &&
        [...head.children].some((x) => x.getAttribute("data-emotion"))
        ? head
        : null;
    });
    // Add custom styles
    const buttonStyle = document.createElement("style");
    buttonStyle.textContent = `#gorgias-chat-messenger-button {
      margin: 16px 16px !important;
      width: 48px;
      height: 48px;
      padding: 0;
    }
    #gorgias-chat-messenger-button > div {
      width: 36px;
      height: 36px;
      left: 3px;
    }`;
    chatButtonHead.appendChild(buttonStyle);
  } catch (error) {
    console.error("Error updating Gorgias styles:", error);
  }
}

updateGorigasStyles();

// Show announcement bar if discount code is applied
// if (
// 	document.cookie.match(/discount_code=(.+?);/) &&
// 	document.cookie.match(/discount_code=(.+?);/)[1] === 'SUMMERSOLSTICE' &&
// 	document.querySelector('.announcement-bar')
// ) {
// 	document.querySelector('.announcement-bar').style.display = 'flex';
// }

function handleProductSwatches() {
  let htmlMap = {};
  function watchSwatches() {
    const swatches = document.querySelectorAll("input[data-product-url");
    const variantPicker = document.querySelector("variant-picker");
    for (let swatch of swatches) {
      const productUrl = swatch.dataset.productUrl;
      if (!htmlMap[productUrl]) {
        htmlMap[productUrl] = fetch(
          `${productUrl}&section_id=${variantPicker.getAttribute("section-id")}`
        ).then((response) => response.text());
      }
      swatch.addEventListener("click", async function () {
        const html = document
          .createRange()
          .createContextualFragment(await htmlMap[productUrl]);
        if (
          new URL(`${window.location.origin}${productUrl}`).pathname !==
          new URL(window.location.href).pathname
        ) {
          const root = document.querySelector(".shopify-section--main-product");
          root.replaceWith(
            html.querySelector(".shopify-section--main-product")
          );
          const url = new URL(`${window.location.origin}${productUrl}`);
          window.history.replaceState({ path: url.href }, "", url.href);
          document.title = `Hammies ${
            document.querySelector(
              ".breadcrumb__item:last-of-type .breadcrumb__link"
            ).textContent
          }`;
          document.querySelector('link[rel="canonical"]').href =
            `${url.origin}${url.pathname}`;
          document.body.dispatchEvent(new CustomEvent("swatch:changed"));
          watchSwatches();
          // Reattach event listeners on product form rerender
          document
            .querySelector("form[is=product-form]")
            ?.addEventListener("product:rerender", () => {
              watchSwatches();
            });
        }
      });
    }
  }
  // Reattach event listeners on product form rerender
  document
    .querySelector("form[is=product-form]")
    ?.addEventListener("product:rerender", () => {
      watchSwatches();
    });
  watchSwatches();
}

handleProductSwatches();

// // Scroll to the second product media on product page
// document.addEventListener("flickity:ready", () => {
//   document.querySelector("flickity-carousel").flickity.then((flickity) => {
//     if (
//       flickity.selectedIndex === 0 &&
//       flickity.cells[1].element.dataset.mediaType === "video"
//     ) {
//       flickity.select(1);
//     }
//   });
// });

// function reloadKlaviyoBIS() {
//   const old = document.querySelector('script[src*="onsite.js"]');
//   if (old) old.remove();
//   document
//     .querySelectorAll(
//       ".klaviyo-bis-trigger," + // the "Notify me" buttons
//         "#klaviyo-bis-modal," + // the modal wrapper
//         "#klaviyo-bis-iframe" // the iframe inside it
//     )
//     .forEach((el) => el.remove());
//   delete window.klaviyo;
//   delete window._klOnsite;
//   delete window.klaviyoBIS;
//   const recorded = [];
//   const origAdd = EventTarget.prototype.addEventListener;
//   EventTarget.prototype.addEventListener = function (type, listener, options) {
//     if (type === "change" && this.matches("body")) {
//       recorded.push({ target: this, type, listener, options });
//       return origAdd.call(this, type, listener, options);
//     }
//   };
//   const script = document.createElement("script");
//   script.src = "https://a.klaviyo.com/media/js/onsite/onsite.js";
//   script.async = true;
//   script.onload = () => {
//     EventTarget.prototype.addEventListener = origAdd;
//     recorded.forEach(({ target, type, listener, options }) => {
//       console.log({ target, type, listener, options });
//       target.removeEventListener(type, listener, options);
//     });
//     // document.addEventListener("variant:changed", function (event) {
//     //   reloadKlaviyoBIS();
//     // });
//     klaviyoBIS.init({
//       account: "WGtENE",
//       platform: "shopify",
//       list: "UZaPEi"
//     });
//     klaviyoBIS.enable("backinstock", {
//       trigger: {
//         product_page_text: "Notify Me",
//         product_page_class: "button button--full button--ternary",
//         product_page_text_align: "center",
//         product_page_margin: "0px",
//         replace_anchor: true
//       },
//       modal: {
//         headline: "{product_name}",
//         body_content:
//           "Register to be notified the instant that this is restocked",
//         email_field_label: "Email",
//         button_label: "Register",
//         subscription_success_label: "You're registered 👍",
//         footer_content: "",
//         newsletter_subscribe_label: "Subscribe to the Hammies newsletter",
//         additional_styles:
//           "@import url('https://fonts.googleapis.com/css?family=Helvetica+Neue'); .modal-title, h1, h2, h3, h4, h5 { text-align: center; font-weight: 700; } p { text-align: center; } .btn { text-transform: uppercase; letter-spacing: 1.2px; border-radius: 4px; } .input-lg { box-shadow: none; padding: 8px; }",
//         drop_background_color: "#000",
//         background_color: "#fff",
//         text_color: "#333",
//         button_text_color: "#fff",
//         button_background_color: "#007eff",
//         close_button_color: "#333",
//         error_background_color: "#fcd6d7",
//         error_text_color: "#C72E2F",
//         success_background_color: "#d3efcd",
//         success_text_color: "#1B9500"
//       }
//     });
//   };
//   document.head.appendChild(script);
// }

// document.addEventListener("variant:changed", function (event) {
//   reloadKlaviyoBIS();
// });

function handleRestockNotificationSubscriptions() {
  function watchRestockNotificationButton() {
    if (document.querySelector("input#restock-notification-email")) {
      document.querySelector("input#restock-notification-email").value =
        window.localStorage.getItem("restock-email") || "";
    }
    document
      .querySelector("#restock-notification-button")
      ?.addEventListener("click", async function (event) {
        // event.preventDefault();
        // event.stopPropagation();
        const button = document.querySelector("#restock-notification-button");
        const email = document.querySelector(
          "input#restock-notification-email"
        )?.value;
        const variant =
          document.querySelector("variant-picker")?.selectedVariant;
        button.setAttribute("aria-busy", true);
        window.localStorage.setItem("restock-email", email);
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=WGtENE`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2024-06-15"
            },
            body: JSON.stringify({
              data: {
                type: "back-in-stock-subscription",
                attributes: {
                  profile: {
                    data: {
                      type: "profile",
                      attributes: {
                        email
                      }
                    }
                  },
                  channels: ["EMAIL"]
                },
                relationships: {
                  variant: {
                    data: {
                      type: "catalog-variant",
                      id: `$shopify:::$default:::${variant.id}`
                    }
                  }
                }
              }
            })
          }
        );
        if (response.ok) {
          button.setAttribute("aria-busy", false);
          button.setAttribute("disabled", true);
          button.className = button.className.replace(
            "button--secondary",
            "button--ternary"
          );
          button.textContent = "You're subscribed!";
        } else {
          button.setAttribute("aria-busy", false);
          button.textContent = "Try again";
          // console.error(response);
        }
      });
  }

  watchRestockNotificationButton();

  // Reattach event listeners on product form rerender
  document
    .querySelector("form[is=product-form]")
    ?.addEventListener("product:rerender", () => {
      watchRestockNotificationButton();
    });

  document.body.addEventListener("swatch:changed", () => {
    watchRestockNotificationButton();
    document
      .querySelector("form[is=product-form]")
      ?.addEventListener("product:rerender", () => {
        watchRestockNotificationButton();
      });
  });
}

handleRestockNotificationSubscriptions();
